import { post, remove } from './../fetch-interceptor';
import { AppThunkAction } from './';
import { Action, Reducer } from 'redux';
import { IEntityStore, StoreHelper, IDeletionResult } from './services/storeHelper';
import { catchApiError, defaultCatch } from "./utils";

export interface CostRateTable {
    id: string;
    name: string;
    description: string;
    color: string;
    isDefault: boolean;
}

export interface CostRateTableState extends IEntityStore<CostRateTable> {
    isLoading: boolean;
    deletionResult?: IDeletionResult[];
}

const unloadedState: CostRateTableState = {
    byId: {},
    allIds: [],
    isLoading: false,
};

interface SaveCostRateTableSuccessAction {
    type: 'SAVE_COST_RATE_TABLE_SUCCESS';
    costRateTable: CostRateTable;
}

interface ReceivedDeleteCostRateTablesResultAction {
    type: 'RECEIVED_REMOVE_COST_RATE_TABLES_RESULT';
    deletionResult?: IDeletionResult[];
}

interface ReorderCostRateTablesSuccessAction {
    type: 'REORDER_COST_RATE_TABLES_SUCCESS';
    ids: string[];
}

type KnownAction = SaveCostRateTableSuccessAction
    | ReceivedDeleteCostRateTablesResultAction
    | ReorderCostRateTablesSuccessAction;

export const actionCreators = {
    createCostRateTable: (name: string, description: string, color: string, isDefault?: boolean): AppThunkAction<KnownAction> => (dispatch, getState) => {
        return post<CostRateTable[]>(`api/costRateTable`, { name, description, color, isDefault: isDefault || false })
            .then(data => {
                data.forEach(table => {
                    dispatch({ type: 'SAVE_COST_RATE_TABLE_SUCCESS', costRateTable: table });
                });
                return data;
            })
            .catch(defaultCatch(dispatch));
    },

    updateCostRateTable: (id: string, name: string, description: string, color: string, isDefault?: boolean): AppThunkAction<KnownAction> => (dispatch, getState) => {
        return post<CostRateTable[]>(`api/costRateTable/${id}`, { name, description, color, isDefault: isDefault || false })
            .then(data => {
                data.forEach(table => {
                    dispatch({ type: 'SAVE_COST_RATE_TABLE_SUCCESS', costRateTable: table });
                });
                return data;
            })
            .catch(defaultCatch(dispatch));
    },

    reorderCostRateTables: (ids: string[]): AppThunkAction<KnownAction> => (dispatch, getState) => {
        return post<CostRateTable[]>(`api/costRateTable/reorder`, { ids })
            .then(data => {
                dispatch({ type: 'REORDER_COST_RATE_TABLES_SUCCESS', ids });
                data.forEach(table => {
                    dispatch({ type: 'SAVE_COST_RATE_TABLE_SUCCESS', costRateTable: table });
                });
                return data;
            })
            .catch(defaultCatch(dispatch));
    },

    deleteCostRateTable: (id: string, onError?: (result: IDeletionResult) => void): AppThunkAction<KnownAction> => (dispatch, getState) => {
        return remove<IDeletionResult>(`api/costRateTable/${id}`)
            .then(result => {
                if (result.isDeleted) {
                    dispatch({ type: 'RECEIVED_REMOVE_COST_RATE_TABLES_RESULT', deletionResult: [result] });
                } else {
                    onError?.(result);
                }
            })
            .catch(catchApiError(error => {
                onError?.({ id, name: '', isDeleted: false, message: error });
            }));
    }
};

export const reducer: Reducer<CostRateTableState> = (state: CostRateTableState = unloadedState, incomingAction: Action) => {
    const action = incomingAction as KnownAction;
    switch (action.type) {
        case 'SAVE_COST_RATE_TABLE_SUCCESS':
            return {
                ...state,
                ...StoreHelper.addOrUpdate(state, action.costRateTable)
            };
        case 'REORDER_COST_RATE_TABLES_SUCCESS':
            return {
                ...state,
                allIds: action.ids
            };
        case 'RECEIVED_REMOVE_COST_RATE_TABLES_RESULT':
            let newState = { ...state };
            action.deletionResult?.forEach(result => {
                if (result.isDeleted) {
                    newState = { ...newState, ...StoreHelper.remove(newState, result.id) };
                }
            });
            return {
                ...newState,
                isLoading: false,
                deletionResult: action.deletionResult
            };
        default:
            return state;
    }
};
